import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIdleSession, broadcastIdleLogout } from "@/hooks/useIdleSession";
import { SESSION_IDLE_CONFIG } from "@/config/session-idle";

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60).toString().padStart(2, "0");
  const s = (total % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Mounts inside <AuthProvider>. Runs one central idle timer and renders the
 * warning modal. Only active when a real user session is present.
 */
export function IdleSessionManager() {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();

  const doLogout = useCallback(async () => {
    try { broadcastIdleLogout(); } catch { /* ignore */ }
    try { queryClient.clear(); } catch { /* ignore */ }
    try { await signOut(); } catch { /* ignore */ }
    if (typeof window !== "undefined") {
      window.location.replace(SESSION_IDLE_CONFIG.expiredRedirect);
    }
  }, [signOut, queryClient]);

  const { phase, remainingMs, stayActive } = useIdleSession({
    enabled: !!user,
    onLogout: doLogout,
  });

  const open = useMemo(() => !!user && phase === "warning", [user, phase]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-md rounded-2xl border border-border/60 bg-background/85 backdrop-blur-xl shadow-2xl"
        aria-live="assertive"
      >
        <AlertDialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldAlert className="h-6 w-6" aria-hidden="true" />
          </div>
          <AlertDialogTitle className="text-center text-lg font-semibold">
            Session expiring soon
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-sm text-muted-foreground">
            For your security, you've been inactive for a while. Your session will expire in:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div
          className="my-4 text-center font-mono text-4xl tabular-nums tracking-wider text-foreground"
          role="timer"
          aria-live="polite"
          aria-label={`Session expires in ${fmt(remainingMs)}`}
        >
          {fmt(remainingMs)}
        </div>

        <AlertDialogFooter className="sm:justify-center gap-2">
          <Button variant="outline" onClick={() => void doLogout()}>
            Sign out now
          </Button>
          <Button autoFocus onClick={stayActive}>
            Stay signed in
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
